import React from 'react';
import styled from 'styled-components';

// A button with no button treatment: the element's own chrome reset away, one focus ring, and
// nothing else.
//
// It exists because the house hierarchy had no answer for "this thing is pressable but must not
// look like a CTA" — a grid cell, a breadcrumb crumb, a disclosure toggle, an icon-sized density
// control. Fourteen files answered it privately with `styled.button`, and the `outline: 2px solid`
// focus rule was hand-copied sixteen times across four different offsets. A focus-treatment change
// was fourteen edits with no way to tell which offsets were deliberate, and a missed file lost its
// ring silently.
//
// Why not the two that look close:
//   - TextButton selects a palette off `theme.themes['light-professional']` / `['dark-professional']`
//     via an isDarkMode prop, so it is blind to the Custom theme, has no :focus-visible rule at all,
//     and forwards no `type`.
//   - InvisibleButton renders a <div>. Not focusable, not a button.
//
// `type="button"` IS THE DEFAULT, and that is the load-bearing part. BaseButton drops `type`, so any
// bare <button> inside a <form> submits it — and several of these live inside the Feed Viewer's
// query form.
//
// The ring's offset is 2px, which is what most callers used. The four that need a different one
// declare it themselves, in their own `&:focus-visible` block — a cell button insets it so the ring
// does not paint over the neighbouring grid column; a row button insets it so it does not overlap
// the row's own border.
//
// This was a `$offset` PROP and could never have worked: styled-components strips every
// $-prefixed prop before handing props to a wrapped COMPONENT, not only before a DOM element, so
// `styled(GhostButton)` with `$offset={-1}` delivered nothing and every inset ring silently
// rendered at +2px. Declaring the exception in CSS is both correct and greppable.
const Base = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 2px;
  }
`;

// A component rather than the bare styled.button, so `type` can default. Everything else — including
// className, which is how `styled(GhostButton)` attaches its own rules — passes straight through.
export const GhostButton = ({ type = 'button', ...rest }) => <Base type={type} {...rest} />;
